import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import usuariosContext from '../../context/usuarios/usuariosContext';
import Routes from '../../utils/Routes';

function Usuarios() {
    const navigate = useNavigate();
    const uContext = useContext(usuariosContext);
    const { usuarios, getAllUsers, addUser, updateUser, deleteUser } = uContext;

    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentUser, setCurrentUser] = useState({
        fullName: '',
        userName: '',
        email: '',
        password: '',
        enabled: true,
        tenantsAllowed: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await getAllUsers();
            setLoading(false);
        };
        fetchData();
    }, []);

    const handleOpenDialog = (user = null) => {
        if (user) {
            setEditMode(true);
            setCurrentUser({
                ...user,
                password: '',
                tenantsAllowed: Array.isArray(user.tenantsAllowed)
                    ? user.tenantsAllowed.map(t => t.value).join(' , ')
                    : user.tenantsAllowed || ''
            });
        } else {
            setEditMode(false);
            setCurrentUser({
                fullName: '',
                userName: '',
                email: '',
                password: '',
                enabled: true,
                tenantsAllowed: ''
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentUser({
            fullName: '',
            userName: '',
            email: '',
            password: '',
            enabled: true,
            tenantsAllowed: ''
        });
    };

    const handleSave = async () => {
        try {
            if (editMode) {
                const updates = { ...currentUser };
                if (!updates.password) delete updates.password;
                await updateUser(updates);
                Swal.fire('Exito', 'Usuario actualizado correctamente', 'success');
            } else {
                await addUser(currentUser);
                Swal.fire('Exito', 'Usuario creado correctamente', 'success');
            }
            handleCloseDialog();
            await getAllUsers();
        } catch (error) {
            Swal.fire('Error', error.message || 'Error al guardar usuario', 'error');
        }
    };

    const handleDelete = async (user) => {
        const result = await Swal.fire({
            title: 'Confirmar eliminacion',
            text: `Esta seguro de eliminar al usuario ${user.userName}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Si, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteUser(user);
                Swal.fire('Eliminado', 'Usuario eliminado correctamente', 'success');
                await getAllUsers();
            } catch (error) {
                Swal.fire('Error', error.message || 'Error al eliminar usuario', 'error');
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate(Routes.abms)} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ color: '#3d4d87', fontWeight: 'bold', flex: 1 }}>
                    Usuarios
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ backgroundColor: '#3d4d87' }}
                >
                    Nuevo Usuario
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell>Nombre Completo</TableCell>
                            <TableCell>Usuario</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell>Tenants Permitidos</TableCell>
                            <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {usuarios?.map((user) => (
                            <TableRow key={user._id} hover>
                                <TableCell>{user.fullName}</TableCell>
                                <TableCell>{user.userName}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={user.enabled ? 'Activo' : 'Inactivo'}
                                        color={user.enabled ? 'success' : 'error'}
                                    />
                                </TableCell>
                                <TableCell>
                                    {Array.isArray(user.tenantsAllowed)
                                        ? user.tenantsAllowed.map(t => t.label).join(', ')
                                        : user.tenantsAllowed || '-'}
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton onClick={() => handleOpenDialog(user)} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(user)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editMode ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Nombre Completo"
                            value={currentUser.fullName}
                            onChange={(e) => setCurrentUser({ ...currentUser, fullName: e.target.value })}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Usuario"
                            value={currentUser.userName}
                            onChange={(e) => setCurrentUser({ ...currentUser, userName: e.target.value })}
                            fullWidth
                            required
                            disabled={editMode}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={currentUser.email}
                            onChange={(e) => setCurrentUser({ ...currentUser, email: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label={editMode ? "Nueva Clave (dejar vacio para mantener)" : "Clave"}
                            type="password"
                            value={currentUser.password}
                            onChange={(e) => setCurrentUser({ ...currentUser, password: e.target.value })}
                            fullWidth
                            required={!editMode}
                        />
                        <TextField
                            label="Tenants Permitidos (separados por coma)"
                            value={currentUser.tenantsAllowed}
                            onChange={(e) => setCurrentUser({ ...currentUser, tenantsAllowed: e.target.value })}
                            fullWidth
                            helperText="Ej: TENANT1 , TENANT2"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={currentUser.enabled}
                                    onChange={(e) => setCurrentUser({ ...currentUser, enabled: e.target.checked })}
                                />
                            }
                            label="Activo"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#3d4d87' }}>
                        {editMode ? 'Guardar Cambios' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Usuarios;
